import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError, get } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { title } from "@/lib/format";

const OP_LABELS = {
  eq: "is", ne: "is not", in: "is any of", nin: "is none of", gte: "≥", lte: "≤",
  between: "between", contains: "contains", exists: "has a value", is_null: "is empty",
  not_null: "is not empty", last_n_days: "in the last N days", before: "before",
  after: "after", today: "is today",
};

const NO_VALUE = ["is_null", "not_null", "exists", "today", "before"];

const emptyCondition = { field: "call_outcome", op: "in", value: [] };

export function ConditionRow({ condition, meta, onChange, onRemove, testId }) {
  const field = meta?.fields?.[condition.field];
  const ops = field?.ops || ["eq"];
  const enumValues = meta?.enums?.[condition.field];
  const refs = condition.field === "list_ids" ? meta?.references?.lists
    : condition.field === "campaign_ids" ? meta?.references?.campaigns
      : condition.field === "assigned_to" ? meta?.references?.users : null;

  const setValue = (value) => onChange({ ...condition, value });

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5" data-testid={testId}>
      <Select value={condition.field} onValueChange={(v) => onChange({ field: v, op: (meta.fields[v]?.ops || ["eq"])[0], value: null })}>
        <SelectTrigger className="h-8 w-44" data-testid={`${testId}-field`}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-64 bg-white">
          {Object.entries(meta?.fields || {}).map(([key, def]) => (
            <SelectItem key={key} value={key}>{def.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={condition.op} onValueChange={(v) => onChange({ ...condition, op: v })}>
        <SelectTrigger className="h-8 w-36" data-testid={`${testId}-op`}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          {ops.map((op) => <SelectItem key={op} value={op}>{OP_LABELS[op] || op}</SelectItem>)}
        </SelectContent>
      </Select>

      {NO_VALUE.includes(condition.op) ? (
        <span className="text-xs text-slate-400">no value needed</span>
      ) : enumValues || refs ? (
        <Select
          value={Array.isArray(condition.value) ? (condition.value[0] || "") : (condition.value ?? "")}
          onValueChange={(v) => setValue(["in", "nin"].includes(condition.op) ? [v] : v)}
        >
          <SelectTrigger className="h-8 w-48" data-testid={`${testId}-value`}><SelectValue placeholder="Select value" /></SelectTrigger>
          <SelectContent className="max-h-64 bg-white">
            {(refs || enumValues || []).map((option) => {
              const value = typeof option === "string" ? option : option.id || option.key;
              const label = typeof option === "string" ? title(option) : option.name;
              return <SelectItem key={value} value={value}>{label}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      ) : (
        <Input
          className="h-8 w-40"
          data-testid={`${testId}-value`}
          value={condition.value ?? ""}
          onChange={(e) => setValue(field?.type === "number" ? Number(e.target.value)
            : field?.type === "bool" ? e.target.value === "true" : e.target.value)}
          placeholder={field?.type === "date" ? "YYYY-MM-DD or days" : "value"}
        />
      )}

      <Button size="sm" variant="ghost" className="ml-auto text-rose-600" onClick={onRemove} data-testid={`${testId}-remove`}>
        Remove
      </Button>
    </div>
  );
}

export function SegmentBuilder({ value, onChange, testId = "segment-builder" }) {
  const meta = useQuery({ queryKey: ["segment-fields"], queryFn: () => get("/segments/fields") });
  const filters = value || { match: "and", conditions: [], groups: [], exclude: [] };
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);

  const patch = (updates) => onChange({ ...filters, ...updates });

  const refreshCount = async () => {
    setCounting(true);
    try {
      const { data } = await api.post("/segments/preview", { filters, limit: 5 });
      setCount(data.count);
    } catch (e) {
      toast.error(apiError(e));
      setCount(null);
    } finally {
      setCounting(false);
    }
  };

  const list = (key) => filters[key] || [];
  const setList = (key, items) => patch({ [key]: items });

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Label className="text-xs">Match</Label>
        <Select value={filters.match || "and"} onValueChange={(v) => patch({ match: v })}>
          <SelectTrigger className="h-8 w-32" data-testid="builder-match"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="and">All conditions (AND)</SelectItem>
            <SelectItem value="or">Any condition (OR)</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={refreshCount} disabled={counting} data-testid="builder-count-button">
          {counting ? "Counting…" : "Refresh count"}
        </Button>
        <span className="text-sm font-medium text-slate-700" data-testid="builder-count">
          {count != null ? `${count.toLocaleString()} contacts match` : "count not calculated"}
        </span>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Include conditions</Label>
        {list("conditions").map((condition, index) => (
          <ConditionRow
            key={index}
            testId={`include-${index}`}
            condition={condition}
            meta={meta.data}
            onChange={(next) => setList("conditions", list("conditions").map((c, i) => (i === index ? next : c)))}
            onRemove={() => setList("conditions", list("conditions").filter((_, i) => i !== index))}
          />
        ))}
        <Button size="sm" variant="outline" onClick={() => setList("conditions", [...list("conditions"), { ...emptyCondition }])} data-testid="builder-add-condition">
          Add condition
        </Button>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">OR groups (any condition inside a group matches)</Label>
        {(filters.groups || []).map((group, gi) => (
          <div key={gi} className="space-y-2 rounded-md border border-dashed border-slate-300 p-2" data-testid={`or-group-${gi}`}>
            {(group.conditions || []).map((condition, index) => (
              <ConditionRow
                key={index}
                testId={`group-${gi}-${index}`}
                condition={condition}
                meta={meta.data}
                onChange={(next) => {
                  const groups = [...filters.groups];
                  groups[gi] = { ...group, conditions: group.conditions.map((c, i) => (i === index ? next : c)) };
                  patch({ groups });
                }}
                onRemove={() => {
                  const groups = [...filters.groups];
                  groups[gi] = { ...group, conditions: group.conditions.filter((_, i) => i !== index) };
                  patch({ groups });
                }}
              />
            ))}
            <Button size="sm" variant="ghost" onClick={() => {
              const groups = [...filters.groups];
              groups[gi] = { ...group, match: "or", conditions: [...(group.conditions || []), { ...emptyCondition }] };
              patch({ groups });
            }} data-testid={`group-${gi}-add`}>
              Add to group
            </Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => patch({ groups: [...(filters.groups || []), { match: "or", conditions: [{ ...emptyCondition }] }] })} data-testid="builder-add-group">
          Add OR group
        </Button>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Exclude conditions</Label>
        {list("exclude").map((condition, index) => (
          <ConditionRow
            key={index}
            testId={`exclude-${index}`}
            condition={condition}
            meta={meta.data}
            onChange={(next) => setList("exclude", list("exclude").map((c, i) => (i === index ? next : c)))}
            onRemove={() => setList("exclude", list("exclude").filter((_, i) => i !== index))}
          />
        ))}
        <Button size="sm" variant="outline" onClick={() => setList("exclude", [...list("exclude"), { field: "do_not_call", op: "eq", value: true }])} data-testid="builder-add-exclude">
          Add exclusion
        </Button>
      </div>
    </div>
  );
}
